import type { ReactElement } from "react";
import { BODY_TYPE, pixelType, type LiftableAction, type ModerationAction } from "odyssey-shared";
import { Button, EmptyState, GLYPH_SIZES, Glyph, GlyphButton, KIT_METRICS, TextLink, logical, centreOffset, type GlyphName } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import type { FamilyStanding } from "./family-client.ts";
import type { FamilySnapshot } from "./family.ts";
import { leftWords } from "./layout.ts";
import { shortWhenWords } from "./words.ts";

/**
 * The Moderation window's Player tab (ADR-056's amendment of 2026-10-06, decisions 3, 13 and 14; the design's
 * `moderation-window` card, a visual reference): who the family is and where its characters are, what stands on it
 * with End ban and End freeze on the row each ends, and the action buttons, over the family's read (P1.4-T90). The
 * window adds the chosen action's fields under the buttons. Rules the card does not set: no level, rank or role name
 * is shown; an action the gateway did not offer is absent; a family this GM may not act on keeps its buttons,
 * disabled, with the gateway's sentence, and its Flag; a family with no character in the world keeps Ban, Flag and
 * the ends, while Kick, Freeze and Warn wait.
 */

/**
 * The actions the Player tab offers, in the card's order, each with its glyph; the Flag sits apart.
 */
const ACTS: readonly { readonly action: Exclude<ModerationAction, "lift" | "feedbackpause" | "feedbackresume" | "feedbackoff" | "feedbackon">; readonly glyph: GlyphName }[] = [
  { action: "warn", glyph: "warning-box" },
  { action: "kick", glyph: "logout" },
  { action: "freeze", glyph: "hourglass-large" },
  { action: "ban", glyph: "user-x" },
];

/**
 * The actions that need a character of the family in the world.
 */
const IN_WORLD: ReadonlySet<ModerationAction> = new Set(["kick", "freeze", "warn"]);

/**
 * When a standing row ends, as its line says it.
 *
 * @param row The row.
 * @param now The page's clock.
 * @returns "12 min left, until 14:17" or "Permanent".
 */
export function standingWhen(row: { readonly until: number | null }, now: number): string {
  return row.until === null ? leftWords(null, now) : say("moderation.window.leftUntil", { left: leftWords(row.until, now), until: say("moderation.window.until", { when: shortWhenWords(row.until, now) }) });
}

/**
 * A ban or a freeze that stands, with its own End button: on the Player tab under its family, and on the In force tab
 * with the family named, where the row also opens that family.
 *
 * @param props The row's `id`, `action`, `issuer`, `until` and `reason`; the `family` to name, or null; whether the
 *   GM `mayEnd` it; whether End is `offered`; `now`; `onEnd`; and `onOpen`, run
 *   when the row is pressed, or undefined.
 * @returns The row.
 */
export function StandingRow({ row, family, mayEnd, offered, now, onEnd, onOpen }: { readonly row: { readonly id: string; readonly action: LiftableAction; readonly issuer: string; readonly until: number | null; readonly reason: string }; readonly family: string | null; readonly mayEnd: boolean; readonly offered: boolean; readonly now: number; readonly onEnd: () => void; readonly onOpen?: () => void }): ReactElement {
  const what = say(`moderation.window.what.${row.action}`);
  const glyph: GlyphName = row.action === "ban" ? "user-x" : "hourglass-large";
  const body = (
    <>
      <span className={`ot-moderation__stand-mark ot-moderation__stand-mark--${row.action}`} style={{ marginTop: logical(centreOffset(STAND_TEXT_HEIGHT, GLYPH_SIZES[glyph])) }}>
        <Glyph name={glyph} />
      </span>
      <span className="ot-moderation__stand-text">
        <span className="ot-moderation__stand-head">
          <span className={`ot-moderation__stand-what ot-moderation__stand-what--${row.action}`}>{family === null ? what : say("moderation.window.familyWhat", { family, what })}</span>
          <span className="ot-moderation__small">{standingWhen(row, now)}</span>
        </span>
        <span className="ot-moderation__muted ot-moderation__small ot-moderation__clip">{say("moderation.window.byRead", { issuer: row.issuer, reason: row.reason })}</span>
      </span>
    </>
  );
  return (
    <div className="ot-moderation__stand-item">
    <div className="ot-moderation__stand ot-moderation__stand--lined" data-moderation="standing">
      {onOpen === undefined ? (
        <span className="ot-moderation__stand-open">{body}</span>
      ) : (
        <button type="button" className="ot-moderation__stand-open ot-moderation__stand-button" aria-label={family === null ? undefined : say("moderation.window.open", { family })} onClick={onOpen}>
          {body}
        </button>
      )}
      {offered ? (
        <span className="ot-moderation__stand-end" style={{ marginTop: logical(centreOffset(STAND_TEXT_HEIGHT, KIT_METRICS.button.height)) }}>
          <Button disabled={!mayEnd} onClick={onEnd}>
            {say(`moderation.window.end.${row.action}`)}
          </Button>
        </span>
      ) : null}
    </div>
      {offered && !mayEnd ? (
        <p className="ot-moderation__warn-line ot-moderation__small" data-moderation="cannot-end">
          <span className="ot-moderation__hint-mark">
            <Glyph name="lock" />
          </span>
          <span>{say("moderation.window.cannotEnd")}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The side of the button that closes the open file, in logical pixels: the kit's small glyph button, inside the
 * family name's line so the head grows by nothing.
 */
const CLOSE_FILE_SIZE = 12;

/**
 * The height of a standing row's text in logical pixels: the what line in the pixel face, a pixel between, and the
 * one line of who issued it. It is the row's inner height, so the mark and the End button are set against it by whole
 * pixels rather than centred by the browser, which leaves them half a pixel off whenever the room left over is odd.
 */
const STAND_TEXT_HEIGHT = pixelType("label").lineHeight + 1 + BODY_TYPE["body-sm"].lineHeight;

/**
 * Who the family is: its name, a staff mark, and each character by first name with where the one in the world is.
 * Compact once an action is chosen: one line with the character in the world. The head ends with the button that
 * closes the file, whatever the read's state, so a name that could not be read can be put away too.
 *
 * @param props The family's `read` and its `name`; whether `compact`; the names of `maps` by id; and `onClose`, which
 *   closes the file.
 * @returns The panel.
 */
function WhoPanel({ snapshot, compact, mapName, onClose }: { readonly snapshot: FamilySnapshot; readonly compact: boolean; readonly mapName: (id: string) => string; readonly onClose: () => void }): ReactElement {
  const read = snapshot.read;
  const name = read?.family ?? snapshot.name ?? "";
  const on = read?.characters.find((character) => character.map !== null) ?? null;
  return (
    <div className="ot-moderation__who ot-panel ot-ui-frame-panel" data-moderation="who">
      <div className="ot-moderation__who-head">
        <span className="ot-moderation__who-mark">
          <Glyph name="users-large" />
        </span>
        <span className="ot-moderation__who-name">{name}</span>
        {compact ? (
          <span className="ot-moderation__who-where ot-moderation__small">{on === null ? say("moderation.window.notOnline") : say("moderation.window.inMap", { first: on.first, map: mapName(on.map as string) })}</span>
        ) : read?.staff === true ? (
          <span className="ot-moderation__chip" data-moderation="staff">
            <span className="ot-moderation__mark ot-moderation__mark--staff">
              <Glyph name="shield" />
            </span>
            {say("moderation.online.marks.staff")}
          </span>
        ) : null}
        <span className="ot-moderation__who-close">
          <GlyphButton glyph="close-small" label={say("moderation.window.closeFile")} size={CLOSE_FILE_SIZE} onClick={onClose} data={{ "data-moderation": "close-file" }} />
        </span>
      </div>
      {compact || read === null ? null : (
        <ul className="ot-moderation__chars">
          {read.characters.map((character) => (
            <li key={character.first} className="ot-moderation__char" data-moderation="character">
              <span className={character.map === null ? "ot-moderation__dot" : "ot-moderation__dot ot-moderation__dot--on"} aria-hidden="true" />
              <span className="ot-moderation__char-first">{character.first}</span>
              <span className={character.map === null ? "ot-moderation__where ot-moderation__muted ot-moderation__small" : "ot-moderation__where ot-moderation__small"}>{character.map === null ? say("moderation.window.offline") : mapName(character.map)}</span>
            </li>
          ))}
        </ul>
      )}
      {snapshot.state === "reading" && read === null ? <p className="ot-moderation__muted ot-moderation__small">{say("moderation.window.reading")}</p> : null}
      {snapshot.state === "missing" ? <p className="ot-moderation__danger ot-moderation__small">{say("moderation.window.missing")}</p> : null}
      {snapshot.state === "failed" || snapshot.state === "rate" ? <p className="ot-moderation__danger ot-moderation__small">{say("moderation.window.unread")}</p> : null}
    </div>
  );
}

/**
 * What the Player tab draws and does.
 */
export interface PlayerTabProps {
  readonly family: FamilySnapshot;
  readonly offered: readonly ModerationAction[];
  readonly chosen: ModerationAction | null;
  readonly busy: boolean;
  readonly narrow: boolean;
  readonly done: { readonly title: string; readonly sentence: string } | null;
  readonly now: number;
  readonly when: (at: number) => string;
  readonly mapName: (id: string) => string;
  readonly onChoose: (action: ModerationAction) => void;
  readonly onClose: () => void;
  readonly onEnd: (row: FamilyStanding) => void;
  readonly onFeedback: () => void;
  readonly onHistory: () => void;
}

/**
 * The Player tab's body above the chosen action's fields.
 *
 * @param props The family's read; the actions offered and the one chosen; whether a request is `busy`; `narrow`; the
 *   the `done` sentence of the last action written, or null; the clock and the writer
 *   of moments; the maps' names; and what the tab's controls do.
 * @returns The body.
 */
export function PlayerTab(props: PlayerTabProps): ReactElement {
  const { family, offered, chosen, busy, narrow, now, when } = props;
  if (family.name === null) {
    return (
      <div className="ot-moderation__pick" data-moderation="pick">
        <EmptyState glyph="user" title={say("moderation.window.pickTitle")}>
          {narrow ? say("moderation.window.pickNarrow") : say("moderation.window.pickWide")}
        </EmptyState>
      </div>
    );
  }
  const read = family.read;
  const name = read?.family ?? family.name;
  const inWorld = read === null ? true : read.characters.some((character) => character.map !== null);
  const actable = read?.actable ?? true;
  const lifts = offered.includes("lift");
  const feedback = read === null || read.feedback.state === "open" ? null : read.feedback;
  const disabled = (action: ModerationAction): boolean => busy || family.state === "missing" || (action !== "alert" && !actable) || (IN_WORLD.has(action) && !inWorld);
  return (
    <>
      <WhoPanel snapshot={family} compact={chosen !== null} mapName={props.mapName} onClose={props.onClose} />
      {props.done === null ? null : (
        <div className="ot-moderation__done" role="status" data-moderation="status">
          <span className="ot-moderation__done-mark">
            <Glyph name="check" />
          </span>
          <span className="ot-moderation__done-text">
            <span className="ot-moderation__done-title">{props.done.title}</span>
            <span>
              {props.done.sentence} <TextLink onClick={props.onHistory}>{say("moderation.window.seeHistory")}</TextLink>
            </span>
          </span>
        </div>
      )}
      {chosen !== null || read === null ? null : (
        <div className="ot-moderation__stands" data-moderation="stands">
          <div className="ot-list__heading ot-moderation__heading">
            <span>{say("moderation.window.inForceOn", { family: name })}</span>
            <span className="ot-list__count">{read.standing.length + (feedback === null ? 0 : 1)}</span>
          </div>
          {read.standing.length === 0 && feedback === null ? <p className="ot-moderation__muted ot-moderation__text">{say("moderation.window.nothingStands")}</p> : null}
          {read.standing.map((row) => (
            <StandingRow key={row.id} row={row} family={null} mayEnd={row.mayEnd} offered={lifts} now={now} onEnd={() => props.onEnd(row)} />
          ))}
          {feedback === null ? null : (
            <div className="ot-moderation__stand ot-moderation__stand--feedback" data-moderation="feedback-state">
              <span className={`ot-moderation__stand-mark ot-moderation__stand-mark--${feedback.state}`}>
                <Glyph name={feedback.state === "paused" ? "clock" : "lock"} />
              </span>
              <span className="ot-moderation__stand-what">{feedback.state === "paused" ? say("moderation.window.feedbackShort.paused", { when: when(feedback.until) }) : say("moderation.window.feedbackShort.off")}</span>
              <span className="ot-moderation__push">
                <TextLink onClick={props.onFeedback}>{say("moderation.window.feedbackLink")}</TextLink>
              </span>
            </div>
          )}
        </div>
      )}
      <div className="ot-moderation__acts" data-moderation="acts">
        <div className="ot-list__heading ot-moderation__heading">
          <span>{chosen === null ? say("moderation.window.actWhole", { family: name }) : say("moderation.window.act", { family: name })}</span>
        </div>
        <div role="group" aria-label={say("moderation.window.actions", { family: name })} className="ot-moderation__act-row">
          {ACTS.filter((act) => offered.includes(act.action)).map((act) =>
            act.action === "ban" && chosen !== "ban" ? (
              <Button key={act.action} variant="danger" icon={act.glyph} disabled={disabled(act.action)} onClick={() => props.onChoose(act.action)}>
                {say(`moderation.actions.${act.action}`)}
              </Button>
            ) : (
              <Button key={act.action} icon={act.glyph} selected={chosen === act.action} disabled={disabled(act.action)} onClick={() => props.onChoose(act.action)}>
                {say(`moderation.actions.${act.action}`)}
              </Button>
            ),
          )}
          {offered.includes("alert") ? (
            <>
              <span className="ot-moderation__act-rule" aria-hidden="true" />
              <Button icon="note" selected={chosen === "alert"} disabled={disabled("alert")} onClick={() => props.onChoose("alert")}>
                {say("moderation.actions.alert")}
              </Button>
            </>
          ) : null}
        </div>
        {read !== null && !actable ? (
          <p className="ot-moderation__warn-line" data-moderation="cannot">
            <span className="ot-moderation__hint-mark">
              <Glyph name="lock" />
            </span>
            <span>{say("moderation.window.cannotAct")}</span>
          </p>
        ) : read !== null && !inWorld ? (
          <p className="ot-moderation__muted ot-moderation__hint" data-moderation="offline">
            <span className="ot-moderation__hint-mark">
              <Glyph name="info-box" />
            </span>
            <span>{say("moderation.window.offlineActs")}</span>
          </p>
        ) : null}
      </div>
    </>
  );
}
